// hooks/useAuth.ts
import { useEffect, useRef, useState } from 'react';
import { onAuthStateChanged, User as FirebaseUser } from 'firebase/auth';
import { auth } from '../firebase/config';
import { firestoreService } from '../services/firestoreService';
import { Role, Tenant, UserProfile, PendingInvite } from '../types';

export interface AuthState {
  user: FirebaseUser | null;
  profile: UserProfile | null;
  tenant: Tenant | null;
  partner: any | null;
  loading: boolean;
  authError: string | null;

  partnerTenantList: Tenant[];
  allTenantList: Tenant[];

  // Shared-device gate
  gateRequired: boolean;
  gateEmail: string | null;
  confirmGate: () => Promise<void>;
  rejectGate: () => Promise<void>;

  updateTenant: (newTenant: Tenant) => Promise<void>;
  pendingInvites: PendingInvite[];
  claimPendingInvite: (inviteId: string) => Promise<void>;
}

function isActiveProfile(p: UserProfile | null): boolean {
  const s = String((p as any)?.status || '').trim().toUpperCase();
  return s === 'ACTIVE';
}

export function useAuthState(): AuthState {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [partner, setPartner] = useState<any | null>(null);
const [loading, setLoading] = useState<boolean>(true);
  const [authError, setAuthError] = useState<string | null>(null);

  const [partnerTenantList, setPartnerTenantList] = useState<Tenant[]>([]);
  const [allTenantList, setAllTenantList] = useState<Tenant[]>([]);
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([]);

  // Shared-device gate state
  const [gateRequired, setGateRequired] = useState<boolean>(false);
  const [gateEmail, setGateEmail] = useState<string | null>(null);
const [gateConfirmedUid, setGateConfirmedUid] = useState<string | null>(null);
  const bootstrapInFlightRef = useRef<boolean>(false);
  const autoClaimAttemptedRef = useRef<boolean>(false);
  const updateTenant = async (newTenant: Tenant) => {
    setTenant(newTenant);
  };

 const confirmGate = async (): Promise<void> => {
  const u = auth.currentUser;
  if (!u) return;

  setAuthError(null);
  setGateRequired(false);
  setGateConfirmedUid(u.uid);
};

  const rejectGate = async (): Promise<void> => {
    try {
      window.localStorage.removeItem('osfn_pending_invite_id');
    } catch (_) {}

    try {
      await auth.signOut();
    } catch (_) {}

    setGateRequired(false);
setGateConfirmedUid(null);
    setGateEmail(null);

    setUser(null);
    setProfile(null);
    setTenant(null);
setPartner(null);
    setPartnerTenantList([]);
    setAllTenantList([]);
    setPendingInvites([]);
    setAuthError(null);
    setLoading(false);
  };

  const claimPendingInvite = async (inviteId: string): Promise<void> => {
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) throw new Error('not_signed_in');

    setLoading(true);
    setAuthError(null);

    try {
      const claimedProfile = await firestoreService.claimInviteById(firebaseUser, inviteId);

      // Token refresh after claim
      await firebaseUser.getIdToken(true);

      setPendingInvites([]);
      setProfile(claimedProfile);

      // Gate is now satisfied for this session
      setGateRequired(false);
setGateConfirmedUid(firebaseUser.uid);
      setGateEmail(String(firebaseUser.email || '').trim().toLowerCase() || null);

      // Do not proceed unless ACTIVE
      if (!isActiveProfile(claimedProfile)) {
        setTenant(null);
setPartner(null);
        setPartnerTenantList([]);
        setAllTenantList([]);
        setAuthError('User inactive or revoked.');
        return;
      }

      if (claimedProfile.tenantId) {
        const tenantData = await firestoreService.getTenantData(claimedProfile.tenantId);
        if (tenantData) setTenant({ ...tenantData, id: claimedProfile.tenantId });
      } else {
        setTenant(null);
      }

      if (claimedProfile.role === Role.PLATFORM_ADMIN) {
        const tenants = await firestoreService.getAllTenants();
        setAllTenantList(tenants);
      } else {
        setAllTenantList([]);
      }

      if (claimedProfile.role === Role.PARTNER_ADMIN && claimedProfile.partnerId) {
  const tenants = await firestoreService.getTenantsForPartner(claimedProfile.partnerId);
  setPartnerTenantList(tenants);

  const p = await firestoreService.getPartnerByPartnerId(claimedProfile.partnerId);
  setPartner(p);
} else {
  setPartnerTenantList([]);
  setPartner(null);
}
    } catch (e: any) {
      console.error('[Auth] claimPendingInvite failed', e);
      setAuthError('Invite claim failed. Contact your admin.');
      setPendingInvites([]);
      setProfile(null);
      setTenant(null);
setPartner(null);
      setPartnerTenantList([]);
      setAllTenantList([]);
    } finally {
      setLoading(false);
    }
  };

  const bootstrapForUser = async (firebaseUser: FirebaseUser): Promise<void> => {
    setLoading(true);
    setAuthError(null);
    setPendingInvites([]);

    try {
      // Deterministic token refresh before any bootstrap reads
      await firebaseUser.getIdToken(true);

      // 1) Try load profile
      const userProfile = await firestoreService.getUserProfile(firebaseUser.uid);

      if (userProfile) {
        console.log(`[Auth] Found existing profile for UID: ${firebaseUser.uid}`);
        setProfile(userProfile);

        if (!isActiveProfile(userProfile)) {
          setTenant(null);
setPartner(null);
          setPartnerTenantList([]);
          setAllTenantList([]);
          setAuthError('User inactive or revoked.');
          setLoading(false);
          return;
        }

        if (userProfile.tenantId) {
          const tenantData = await firestoreService.getTenantData(userProfile.tenantId);
          if (tenantData) setTenant({ ...tenantData, id: userProfile.tenantId });
        } else {
          setTenant(null);
        }

        if (userProfile.role === Role.PLATFORM_ADMIN) {
          const tenants = await firestoreService.getAllTenants();
          setAllTenantList(tenants);
        } else {
          setAllTenantList([]);
        }

       if (userProfile.role === Role.PARTNER_ADMIN && userProfile.partnerId) {
  const tenants = await firestoreService.getTenantsForPartner(userProfile.partnerId);
  setPartnerTenantList(tenants);

  try {
  const p = await firestoreService.getPartnerByPartnerId(userProfile.partnerId);
  setPartner(p);
}catch (e) {
  console.warn('[Auth] partner load failed (non-fatal)', e);
  setPartner(null);
}
} else {
  setPartnerTenantList([]);
  setPartner(null);
}

        setLoading(false);
        return;
      }
      // 2) No profile: invite flow only
      console.warn(
        `[Auth] No profile found for UID: ${firebaseUser.uid}. Checking pending invitations for ${firebaseUser.email}...`
      );

      const invites = await firestoreService.listMyPendingInvites(firebaseUser);

      if (invites.length === 1 && invites[0]?.inviteId) {
  if (autoClaimAttemptedRef.current) {
    // Already attempted once this session; do not repeat auto-claim
    setPendingInvites(invites);
    setProfile(null);
    setTenant(null);
setPartner(null);
    setPartnerTenantList([]);
    setAllTenantList([]);
    setAuthError(null);
    setLoading(false);
    return;
  }
  autoClaimAttemptedRef.current = true;

  console.warn('[Auth] Exactly one pending invite found. Auto-claiming...');
  const claimedProfile = await firestoreService.claimInviteById(firebaseUser, String(invites[0].inviteId));
  await firebaseUser.getIdToken(true);

  setPendingInvites([]);
  setProfile(claimedProfile);

  if (!isActiveProfile(claimedProfile)) {
    setTenant(null);
    setPartnerTenantList([]);
    setAllTenantList([]);
    setAuthError('User inactive or revoked.');
    setLoading(false);
    return;
  }

  if (claimedProfile.tenantId) {
    const tenantData = await firestoreService.getTenantData(claimedProfile.tenantId);
    if (tenantData) setTenant({ ...tenantData, id: claimedProfile.tenantId });
  } else {
    setTenant(null);
  }

  if (claimedProfile.role === Role.PLATFORM_ADMIN) {
    const tenants = await firestoreService.getAllTenants();
    setAllTenantList(tenants);
  } else {
    setAllTenantList([]);
  }

  if (claimedProfile.role === Role.PARTNER_ADMIN && claimedProfile.partnerId) {
    const tenants = await firestoreService.getTenantsForPartner(claimedProfile.partnerId);
    setPartnerTenantList(tenants);
  } else {
    setPartnerTenantList([]);
  }
if (claimedProfile.role === Role.PARTNER_ADMIN && claimedProfile.partnerId) {
  const p = await firestoreService.getPartnerByPartnerId(claimedProfile.partnerId);
  setPartner(p);
} else {
  setPartner(null);
}
  setLoading(false);
  return;
}

      if (invites.length > 1) {
        console.warn('[Auth] Multiple pending invites found. Waiting for user selection.');
        setPendingInvites(invites);
        setProfile(null);
        setTenant(null);
setPartner(null);
        setPartnerTenantList([]);
        setAllTenantList([]);
        setAuthError(null);
        setLoading(false);
        return;
      }

      // 0 invites: shared-device safe behavior => sign out, show deterministic error
      console.error('[Auth] No pending invitations found for token email.');
      setPendingInvites([]);
      setProfile(null);
      setTenant(null);
setPartner(null);
      setPartnerTenantList([]);
      setAllTenantList([]);

      try {
        window.localStorage.removeItem('osfn_pending_invite_id');
      } catch (_) {}

      try {
        await auth.signOut();
      } catch (_) {}

      setAuthError('No user profile found and no pending invitation found.');
      setLoading(false);
    } catch (e: any) {
      console.error('[Auth] Auth bootstrap error:', e);
      setAuthError('Auth bootstrap error.');
      setProfile(null);
      setTenant(null);
setPartner(null);
      setPartnerTenantList([]);
      setAllTenantList([]);
      setPendingInvites([]);
      setLoading(false);
    }
  };

  // When gate is confirmed for the CURRENT uid, run bootstrap exactly once (single-flight)
  useEffect(() => {
    const u = auth.currentUser;
    if (!u) return;
    if (gateConfirmedUid !== u.uid) return;

    if (bootstrapInFlightRef.current) return;
    bootstrapInFlightRef.current = true;

    (async () => {
      try {
        await bootstrapForUser(u);
      } finally {
        bootstrapInFlightRef.current = false;
      }
    })();
  }, [gateConfirmedUid]);

  // Gate-only auth listener (no reads here)
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      // Signed-out should be stable and cheap
      if (!firebaseUser) {
        setUser(null);
        setProfile(null);
        setTenant(null);
setPartner(null);
        setPartnerTenantList([]);
        setAllTenantList([]);
        setPendingInvites([]);
        setAuthError(null);

        setGateRequired(false);
        setGateConfirmedUid(null);
        setGateEmail(null);

        setLoading(false);
        return;
      }

      // Signed-in session exists => enter gate mode (no bootstrap here)
      setLoading(false);
      setAuthError(null);
      setPendingInvites([]);

      setUser(firebaseUser);

      // reset confirmation and auto-claim guard on any auth change
      setGateConfirmedUid(null);
      autoClaimAttemptedRef.current = false;

      const em = String(firebaseUser.email || '').trim().toLowerCase() || null;
      setGateEmail(em);
      setGateRequired(true);

      // Reset app state until Continue
      setProfile(null);
      setTenant(null);
setPartner(null);
      setPartnerTenantList([]);
      setAllTenantList([]);
    });

    return () => unsubscribe();
  }, []);

  return {
    user,
    profile,
    tenant,
partner,
    loading,
    authError,
    partnerTenantList,
    allTenantList,

    gateRequired,
    gateEmail,
    confirmGate,
    rejectGate,

    updateTenant,
    pendingInvites,
    claimPendingInvite,
  };
}